import { primitives as p } from './primitives.ts'

/**
 * Semantic colour tokens — docs/04-design-system.md §2.2.
 *
 * Keys are the flattened camelCase form of the dotted names in the doc:
 * `bg.canvas` -> `bgCanvas`, `accent.onPrimary` -> `accentOnPrimary`.
 *
 * Both themes must define every token, so no component branches on theme.
 * The shared `SemanticColors` type makes a missing key a compile error.
 */
export type SemanticColors = {
  /** Screen background */
  bgCanvas: string
  /** Cards, sheets, list rows */
  bgSurface: string
  /** Modals, menus over surfaces */
  bgSurfaceRaised: string
  /** Section fills, input backgrounds */
  bgSubtle: string
  /** Tooltips, snackbars */
  bgInverse: string

  /** Dividers, card outlines */
  borderDefault: string
  /** Input borders */
  borderStrong: string
  /** Focus ring */
  borderFocus: string

  /** Body and headings */
  textPrimary: string
  /** Supporting text */
  textSecondary: string
  /** Metadata, timestamps */
  textTertiary: string
  /** Text on filled buttons */
  textInverse: string
  /** Disabled labels */
  textDisabled: string

  /** Primary actions, active tab */
  accentPrimary: string
  /** Pressed state */
  accentPrimaryHover: string
  /** Selected row, badge fill */
  accentPrimarySubtle: string
  /** Text on accentPrimary */
  accentOnPrimary: string
  /** Highlights, recommended plan, free badge */
  accentGold: string
  /** Gold badge fill */
  accentGoldSubtle: string

  /** Completed, paid, downloaded */
  statusSuccess: string
  /** Expiring, pending, offline */
  statusWarning: string
  /** Errors, failed, banned */
  statusError: string
  /** Neutral information */
  statusInfo: string
  /** Subscription-locked content */
  statusLocked: string

  /** Behind modals and drawers */
  overlayScrim: string
}

export const lightColors: SemanticColors = {
  bgCanvas: p.brand.lightBlueGray,
  bgSurface: p.brand.white,
  bgSurfaceRaised: p.brand.white,
  bgSubtle: p.slate[100],
  bgInverse: p.brand.deepNavy,

  borderDefault: p.slate[200],
  borderStrong: p.slate[300],
  borderFocus: p.blue[500],

  textPrimary: p.brand.deepNavy,
  textSecondary: p.slate[600],
  textTertiary: p.slate[500],
  textInverse: p.brand.white,
  textDisabled: p.slate[400],

  accentPrimary: p.blue[600],
  accentPrimaryHover: p.blue[700],
  accentPrimarySubtle: p.blue[50],
  accentOnPrimary: p.brand.white,
  accentGold: p.gold[500],
  accentGoldSubtle: p.gold[50],

  statusSuccess: p.green[600],
  statusWarning: p.amber[500],
  statusError: p.red[500],
  statusInfo: p.blue[600],
  statusLocked: p.slate[400],

  overlayScrim: 'rgba(0,0,0,0.45)',
}

export const darkColors: SemanticColors = {
  bgCanvas: p.brand.deepNavy,
  bgSurface: p.brand.navySurface,
  bgSurfaceRaised: p.slate[800],
  bgSubtle: p.slate[800],
  bgInverse: p.brand.lightBlueGray,

  borderDefault: p.slate[700],
  borderStrong: p.slate[600],
  borderFocus: p.blue[400],

  textPrimary: p.brand.lightBlueGray,
  textSecondary: p.brand.coolGray,
  textTertiary: p.slate[400],
  textInverse: p.brand.deepNavy,
  textDisabled: p.slate[500],

  accentPrimary: p.blue[400],
  accentPrimaryHover: p.blue[300],
  accentPrimarySubtle: p.blue[950],
  accentOnPrimary: p.brand.deepNavy,
  accentGold: p.gold[300],
  accentGoldSubtle: p.gold[900],

  statusSuccess: p.green[400],
  statusWarning: p.gold[300],
  // Dark-theme-only tint; no primitive equivalent (docs/04 §2.2).
  statusError: '#F2857F',
  statusInfo: p.blue[400],
  statusLocked: p.slate[500],

  overlayScrim: 'rgba(0,0,0,0.65)',
}

export type ThemeName = 'light' | 'dark'

export const colors: Record<ThemeName, SemanticColors> = {
  light: lightColors,
  dark: darkColors,
}

export function getColors(theme: ThemeName): SemanticColors {
  return colors[theme]
}
